// TODO(ts-migration): large card shared by CompanyPage and SettingPage; convert on its next rework.
import {
  Box,
  Paper,
  Typography,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  FitnessCenter as FitnessCenterIcon,
  Assignment as AssignmentIcon,
  MonitorWeight as MonitorWeightIcon,
  Restaurant as RestaurantIcon,
  Edit as EditIcon,
  SwapHoriz as SwapHorizIcon,
  Brightness6 as Brightness6Icon,
} from "@mui/icons-material";
import {useState} from "react";
import {updateCompanySettings} from "../../services/system/CompanyService";
import {updateOwnCompanySettings} from "../../services/CompanyService";
import {useAuth} from "../../contexts/AuthContext";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {RTL} from "../../cache/CacheRtl";
import {t} from "../dashboard/coach/coachTokens";

const CompanySettingsSection = ({company, setLoadCompany}) => {
  const [saving, setSaving] = useState(false);
  const {isSuperAdmin} = useAuth();
  const {notificationHandler} = useMessageContext();

  const saveSettings = async patch => {
    setSaving(true);
    try {
      // Send ONLY the toggle that changed: both endpoints patch, leaving omitted fields untouched.
      // The old call resent a hand-listed whitelist because /admin/company is a blind overwrite that
      // nulls anything absent.
      //
      // Which endpoint depends on WHO is saving, because this card renders on two pages:
      //   - CompanyPage (super-admin drilling into a company by id) → /system/company/{id}/settings,
      //     which needs the id since a super-admin has no company of their own to scope to.
      //   - SettingPage (a company admin editing their OWN company) → /admin/company/settings,
      //     scoped from the principal.
      // They are not interchangeable: the /system route is SYSTEM_ADMIN-only (a company admin got a
      // 401 — the bug this branch fixes), and the /admin route 403s for a company-less super-admin.
      await (isSuperAdmin()
          ? updateCompanySettings(company.id, patch)
          : updateOwnCompanySettings(patch));
      notificationHandler.success('הגדרות עודכנו בהצלחה');
      setLoadCompany(prv => !prv);
    } catch (e) {
      notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field, currentValue) => saveSettings({[field]: !currentValue});

  const handleThemeChange = (_e, value) => {
    // ToggleButtonGroup reports null when the selected button is clicked again; that is not a choice.
    if (!value || value === defaultAppTheme) return;
    saveSettings({defaultAppTheme: value});
  };

  const useSystemExercises = company?.useSystemExercises ?? true;
  const useSystemTemplates = company?.useSystemTemplates ?? true;
  const showOriginalRepsRange = company?.showOriginalRepsRange ?? false;
  const repsInputRangeDefault = company?.repsInputRangeDefault ?? true;
  const allowTraineeChangeRepsInputRange = company?.allowTraineeChangeRepsInputRange ?? false;
  // Permissive default, matching the server resolver: only an explicit false blocks the trainee.
  const allowTraineeManageWeightTargets = company?.allowTraineeManageWeightTargets ?? true;
  // RESTRICTIVE default, and the only one on this card — again matching the server resolver
  // (CompanyNutritionDisplay): only an explicit true shows points. The flag redefines what every
  // figure in the app's nutrition tab MEANS, so a company that never asked must never get it.
  const nutritionPointsEnabled = company?.nutritionPointsEnabled ?? false;
  const allowTraineeEditWorkout = company?.allowTraineeEditWorkout ?? true;
  const allowTraineeReplaceExercise = company?.allowTraineeReplaceExercise ?? true;
  // Matches the server resolver (CompanyAppTheme): anything but an explicit LIGHT is the original
  // dark look. Only the default — each trainee can still switch on their own phone.
  const defaultAppTheme = company?.defaultAppTheme === 'LIGHT' ? 'LIGHT' : 'DARK';

  return (
      <Paper
          sx={{
            backgroundColor: t.white,
            border: `1px solid ${t.border}`,
            borderRadius: t.r,
            boxShadow: t.sh,
            overflow: "hidden",
            direction: "rtl"
          }}
          elevation={0}
      >
        {/* Header */}
        <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              padding: {xs: "12px 16px", md: "14px 18px"},
              backgroundColor: t.bg,
              borderBottom: `1px solid ${t.border}`
            }}
        >
          <Box sx={{width: 36, height: 36, borderRadius: "10px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
            <SettingsIcon sx={{color: t.t2, fontSize: 20}}/>
          </Box>
          <Box>
            <Typography sx={{fontWeight: 800, fontSize: 15, color: t.text, letterSpacing: "-0.2px"}}>הגדרות חברה</Typography>
            <Typography sx={{fontSize: 11.5, color: t.t3, fontWeight: 600}}>העדפות תצוגה, תזונה ותרגילים</Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{padding: {xs: "16px", md: "20px"}, display: "flex", flexDirection: "column", gap: "12px"}}>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <FitnessCenterIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    שימוש בתרגילים של פרוגרס
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר כבוי, יוצגו רק התרגילים של החברה
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={useSystemExercises}
                  onChange={() => handleToggle('useSystemExercises', useSystemExercises)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <AssignmentIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    שימוש בתבניות של פרוגרס
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר כבוי, יוצגו רק התבניות של החברה
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={useSystemTemplates}
                  onChange={() => handleToggle('useSystemTemplates', useSystemTemplates)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <FitnessCenterIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    הצגת טווח חזרות מקורי
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר פעיל, מתאמנים רואים את טווח החזרות שהוגדר בתוכנית
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={showOriginalRepsRange}
                  onChange={() => handleToggle('showOriginalRepsRange', showOriginalRepsRange)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <FitnessCenterIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    הצגת טווח חזרות בשדה החזרות
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר פעיל, שדה החזרות מציג טווח (למשל 9 - 11) במקום מספר בודד
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={repsInputRangeDefault}
                  onChange={() => handleToggle('repsInputRangeDefault', repsInputRangeDefault)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <FitnessCenterIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    אפשר למתאמן לשנות את טווח החזרות
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר פעיל, המתאמן יכול להחליט בעצמו אם להציג טווח בשדה החזרות
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={allowTraineeChangeRepsInputRange}
                  onChange={() => handleToggle('allowTraineeChangeRepsInputRange', allowTraineeChangeRepsInputRange)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <MonitorWeightIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    אפשר למתאמן לנהל את יעדי המשקל שלו
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר כבוי, המתאמן רואה את המטרה והיעד השבועי אך רק המאמן יכול לשנות אותם
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={allowTraineeManageWeightTargets}
                  onChange={() => handleToggle('allowTraineeManageWeightTargets', allowTraineeManageWeightTargets)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <EditIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    עריכת אימון על ידי המתאמן
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    מאפשר למתאמן להוסיף, להסיר ולסדר תרגילים באימון שלו. כאשר כבוי, מצב העריכה נעול
                    והאימון נשאר כפי שהמאמן בנה אותו
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={allowTraineeEditWorkout}
                  onChange={() => handleToggle('allowTraineeEditWorkout', allowTraineeEditWorkout)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <SwapHorizIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    החלפת תרגיל על ידי המתאמן
                  </Typography>
                  {/* The caveat is deliberate: with edit mode open a trainee can delete one exercise
                      and add another, which the server cannot tell apart from an ordinary
                      remove-plus-add. Promising more than that here would be a lie. */}
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    מאפשר למתאמן להחליף תרגיל בתרגיל אחר. כדי למנוע החלפה לגמרי, יש לכבות גם את
                    עריכת האימון
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={allowTraineeReplaceExercise}
                  onChange={() => handleToggle('allowTraineeReplaceExercise', allowTraineeReplaceExercise)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <RestaurantIcon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    הצגת תזונה בנקודות
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    כאשר פעיל, התזונה מוצגת בנקודות במקום קלוריות וגרמים (נקודה = 100 קלוריות) — גם לך וגם למתאמנים. סיבים ומשקל המזון נשארים בגרמים
                  </Typography>
                </Box>
              </Box>
              <Switch
                  checked={nutritionPointsEnabled}
                  onChange={() => handleToggle('nutritionPointsEnabled', nutritionPointsEnabled)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                  }}
              />
            </Box>
          </RTL>
          <RTL>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  padding: "12px 14px",
                  backgroundColor: t.bg,
                  border: `1px solid ${t.borderLight}`,
                  borderRadius: t.rs,
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <Brightness6Icon sx={{color: t.t2, fontSize: "18px"}}/>
                <Box>
                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text}}>
                    ערכת צבעים באפליקציה
                  </Typography>
                  <Typography sx={{fontSize: "12px", color: t.t3}}>
                    ברירת המחדל שבה האפליקציה נפתחת אצל המתאמנים. כל מתאמן יכול לשנות אותה בהגדרות הפרופיל שלו
                  </Typography>
                </Box>
              </Box>
              <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={defaultAppTheme}
                  onChange={handleThemeChange}
                  disabled={saving}
                  aria-label="ערכת צבעים באפליקציה"
                  sx={{
                    flexShrink: 0,
                    direction: "ltr",
                    "& .MuiToggleButton-root": {px: 1.5, fontSize: "12px", fontWeight: 600, color: t.t2},
                    "& .MuiToggleButton-root.Mui-selected": {color: t.white, backgroundColor: t.green},
                    "& .MuiToggleButton-root.Mui-selected:hover": {backgroundColor: t.green},
                  }}
              >
                <ToggleButton value="LIGHT">בהיר</ToggleButton>
                <ToggleButton value="DARK">כהה</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </RTL>
        </Box>
      </Paper>
  );
};

export default CompanySettingsSection;
